<template>
  <div class="balance py-6 px-8">
    <h-header back="../missions" />

    <h-loading v-if="isLoading" />

    <div v-else>
      <v-row class="filters--list mb-4">
        <v-col cols="12" sm="12" md="3" lg="4">
          <v-select
            v-model="filters.completedFilter"
            @change="applyCompletedFilter"
            :items="[
              { label: $t('mission.filters.inProgress'), value: false },
              { label: $t('mission.filters.completed'), value: true },
            ]"
            :label="$t('mission.filters.completed')"
            :item-text="'label'"
            :item-value="'value'"
            outlined
            small
            dense
          />
        </v-col>
      </v-row>

      <v-card outlined>
        <v-data-table
          disable-sort
          hide-default-footer
          mobile-breakpoint="20"
          :headers="headers"
          :loading="isLoading"
          :items="missionHostsProgress"
          :items-per-page="itemsPerPage"
          :no-data-text="$t('appointment.table.noData')"
          :loading-text="$t('appointment.table.loading')"
        >
          <template v-slot:[`item.services_done`]="{ item }">
            <v-row no-gutters>
              <v-col cols="4">
                <span class="d-block text-end caption mr-2">
                  {{ `${getMissionProgressPercentage(item)}%` }}
                </span>
              </v-col>
              <v-col cols="8">
                <v-progress-linear
                  height="13"
                  class="mt-1"
                  :color="getProgressBarColor(item)"
                  :value="getMissionProgressPercentage(item)"
                  rounded
                >
                </v-progress-linear>
              </v-col>
            </v-row>
          </template>
          <template v-slot:[`item.host_email`]="{ item }">
            <span class="d-block body-2">
              {{ hasData(item.host_name) }}
            </span>
            <span class="caption grey--text">
              {{ hasData(item.host_email) }}
            </span>
          </template>
          <template v-slot:[`item.total_mission_services`]="{ item }">
            <span class="body-2">
              {{ getServicesDone(item) }}
            </span>
          </template>
          <template v-slot:[`item.ending_date`]="{ item }">
            <span class="body-2">
              {{ formatDate(item.ending_date) }}
            </span>
          </template>
          <template v-slot:[`item.payment_id`]="{ item }">
            <v-chip v-if="item.payment_id">
              <small class="caption">
                {{ item.payment_id }}
              </small>
              <CopyToClipboard :value="item.payment_id" />
            </v-chip>
            <span v-else>---</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              v-if="!disablePaymentButton(item)"
              @click="openConfirmModal(item)"
              color="primary"
              outlined
              small
            >
              <v-icon left> mdi-cash-check </v-icon>
              <span class="body-2">
                {{ $t('mission.actions.pay') }}
              </span>
            </v-btn>
            <span v-else class="body-2 grey--text text--lighten-1">---</span>
          </template>
        </v-data-table>
      </v-card>

      <h-pagination
        :fetch="fetchMissionHostsProgress"
        :requireOrderByFilter="false"
        :fetchParams="[missionId]"
      />
    </div>

    <ModalMissionPayConfirm @confirm="pay" ref="modalMissionPay" />
  </div>
</template>
<script>
import { missionProgressHeaders } from './listHeaders/missionHeaders'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { catalogDate } from '@/util/catalog'
import store from '@/store'

import CopyToClipboard from '@/components/app/CopyToClipboard'
import ModalMissionPayConfirm from '@/components/missions/modals/ModalMissionPayConfirm'

export default {
  name: 'MissionHostProgress',

  props: {
    missionId: {
      required: true,
    },
  },

  components: {
    CopyToClipboard,
    ModalMissionPayConfirm,
  },

  data() {
    return {
      filters: { completedFilter: false },
      headers: missionProgressHeaders,
      formatDate: catalogDate,
    }
  },

  computed: {
    ...mapGetters([
      'mission',
      'isLoading',
      'itemsPerPage',
      'missionHostsProgress',
    ]),
  },

  methods: {
    ...mapActions([
      'payMission',
      'fetchMission',
      'showErrorMessage',
      'showSuccessMessage',
      'fetchMissionHostsProgress',
    ]),
    ...mapMutations(['setFilters', 'setIsLoading']),

    getMissionProgressPercentage(item) {
      var servicesDonePercentage =
        (item.services_done * 100) / this.mission.service_quantity
      return servicesDonePercentage > 100
        ? 100
        : Math.ceil(servicesDonePercentage)
    },
    getServicesDone(item) {
      return `${item.services_done}/${this.mission.service_quantity}`
    },

    getProgressBarColor(item) {
      if (this.getMissionProgressPercentage(item) >= 100) return 'green'
      return 'orange'
    },

    applyCompletedFilter() {
      Promise.resolve(
        this.setFilters({ ...store.getters.filters, ...this.filters })
      ).then(() => this.fetchMissionHostsProgress(this.missionId))
    },

    buildPayBody(missionProgress) {
      return {
        host_id: missionProgress.host_id,
        mission_id: this.missionId,
      }
    },

    async pay(missionProgress) {
      this.payMission(this.buildPayBody(missionProgress))
        .then(({ data }) => {
          this.showSuccessMessage(this.$t('mission.label.paymentSuccess'))
          this.fetchMissionHostsProgress(this.missionId)
        })
        .catch(({ data }) => {
          this.showErrorMessage(data.message)
        })
        .finally(() => {
          this.setIsLoading(false)
        })
    },

    disablePaymentButton(missionProgress) {
      return (
        missionProgress.payment_id != null ||
        missionProgress.services_done < this.mission.service_quantity
      )
    },

    openConfirmModal(missionProgress) {
      this.$refs.modalMissionPay.openConfirmModal(missionProgress, this.mission)
    },

    hasData(data) {
      return data ? data : '---'
    }
  },

  mounted() {
    this.fetchMission(this.missionId)
    this.fetchMissionHostsProgress(this.missionId)
  },
}
</script>
